/**
 * Firma reproducible de un análisis (constitución, Art. 10 · ADR-005).
 *
 * Dos informes con la misma firma son el mismo informe. Uno impreso hace tres
 * meses se puede volver a producir idéntico si se conserva la entrada.
 */

import type { GuardiaHistoryRecord } from "../contracts/telemetry.ts";
import { DIGEST_EXCLUDED_FIELDS } from "../contracts/telemetry.ts";
import { LAB_PROFILE } from "./lab-profile.ts";
import { DRIVING_ANALYSIS_VERSION, PIPELINE_VERSION } from "./driving-analysis-v5.ts";

/**
 * JSON con las claves ordenadas, recursivamente.
 *
 * Sin esto, dos objetos con el mismo contenido y distinto orden de claves
 * darían hashes distintos, y la firma dejaría de significar nada. `JSON.stringify`
 * conserva el orden de inserción, que depende de cómo se construyó el objeto.
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`).join(",")}}`;
}

export async function sha256(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Huella de los registros de entrada. Se excluye `ingested_at`: es cuándo se
 * leyó el dato, no el dato. Incluirlo haría que el mismo recorrido cambiara de
 * firma en cada consulta y la caché nunca acertaría.
 */
export async function computeInputDigest(records: GuardiaHistoryRecord[]): Promise<string> {
  const stripped = records.map((record) => {
    const copy: Record<string, unknown> = { ...record };
    for (const field of DIGEST_EXCLUDED_FIELDS) delete copy[field];
    return copy;
  });
  return sha256(canonicalJson(stripped));
}

/**
 * Huella del perfil con el que se calculó el análisis.
 *
 * Recibe el perfil, no lo asume. Desde que existe la familia por cadencia
 * (`profiles.ts`), dar por sentado `LAB_PROFILE` haría que dos informes
 * calculados con umbrales distintos compartieran huella — y la firma dejaría de
 * distinguir justo lo que tiene que distinguir: no son comparables aunque
 * vengan del mismo vehículo y del mismo período.
 */
export async function computeProfileDigest(profile: unknown = LAB_PROFILE): Promise<string> {
  return sha256(canonicalJson(profile));
}

export type AnalysisSignatureInput = {
  deviceId: string;
  requestedFrom: string;
  requestedTo: string;
  timezone: string;
  inputDigest: string;
  profileDigest: string;
};

export async function computeAnalysisSignature(input: AnalysisSignatureInput): Promise<string> {
  return sha256(
    canonicalJson({
      engineVersion: DRIVING_ANALYSIS_VERSION,
      pipelineVersion: PIPELINE_VERSION,
      profileDigest: input.profileDigest,
      deviceId: input.deviceId,
      period: {
        fromIso: input.requestedFrom,
        toIso: input.requestedTo,
        timezone: input.timezone,
      },
      inputDigest: input.inputDigest,
    }),
  );
}

export type AnalysisProvenance = {
  analysisSignature: string;
  inputDigest: string;
  profileDigest: string;
  engineVersion: typeof DRIVING_ANALYSIS_VERSION;
  pipelineVersion: typeof PIPELINE_VERSION;
};

/** Procedencia completa de un análisis, lista para imprimir en el informe. */
export async function buildProvenance(
  records: GuardiaHistoryRecord[],
  input: {
    deviceId: string;
    requestedFrom: string;
    requestedTo: string;
    timezone: string;
    /** El perfil realmente aplicado. Sin él la firma no distingue umbrales. */
    profile: unknown;
  },
): Promise<AnalysisProvenance> {
  const inputDigest = await computeInputDigest(records);
  const profileDigest = await computeProfileDigest(input.profile);
  return {
    analysisSignature: await computeAnalysisSignature({ ...input, inputDigest, profileDigest }),
    inputDigest,
    profileDigest,
    engineVersion: DRIVING_ANALYSIS_VERSION,
    pipelineVersion: PIPELINE_VERSION,
  };
}
